import * as React from 'react'
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  isSameMonth,
  isSameDay,
  isAfter,
  isBefore,
} from 'date-fns'

type DateCalendarType = 'singleDate' | 'rangeDate' | 'startDate'

interface DateCalendarProps {
  currentMonth: Date
  startDate: Date | null
  endDate?: Date | null
  hoverDate?: Date | null
  type: DateCalendarType
  handleDayClick: (day: Date) => void
  setHoverDate?: (date: Date | null) => void
  
  compact?: boolean
}
export const DateCalendar: React.FC<DateCalendarProps> = ({
  currentMonth,
  startDate,
  endDate,
  hoverDate,
  handleDayClick,
  setHoverDate,
  type,
  compact = false,
}) => {
  const isRangeType = type === 'rangeDate'
  const isStartDate = type === 'startDate'
  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(monthStart)
  const startCalendar = startOfWeek(monthStart, { weekStartsOn: 1 })
  const endCalendar = endOfWeek(monthEnd, { weekStartsOn: 1 })

  const isInRange = (day: Date) => {
    if (startDate && !endDate && hoverDate) {
      const [from, to] = isBefore(hoverDate, startDate)
        ? [hoverDate, startDate]
        : [startDate, hoverDate]
      return (
        (isAfter(day, from) || isSameDay(day, from)) &&
        (isBefore(day, to) || isSameDay(day, to))
      )
    }

    if (startDate && endDate) {
      return (
        (isAfter(day, startDate) || isSameDay(day, startDate)) &&
        (isBefore(day, endDate) || isSameDay(day, endDate))
      )
    }

    return false
  }

  const days: React.JSX.Element[] = []
  let day = startCalendar
  while (day <= endCalendar) {
    for (let i = 0; i < 7; i++) {
      const cloneDay = new Date(day)
      const selectedStart = startDate && isSameDay(cloneDay, startDate)
      const selectedEnd = endDate && isSameDay(cloneDay, endDate)
      const isDayInRange = isInRange(cloneDay)

      const isCandidateForEndDate =
        startDate &&
        !endDate &&
        isAfter(cloneDay, startDate) &&
        isSameMonth(cloneDay, monthStart)

      const isHoveringRange =
        startDate &&
        !endDate &&
        hoverDate &&
        ((isAfter(cloneDay, startDate) && isBefore(cloneDay, hoverDate)) ||
          (isBefore(cloneDay, startDate) && isAfter(cloneDay, hoverDate)))

      days.push(
        <div
          key={cloneDay.toString()}
          onClick={() => handleDayClick(cloneDay)}
          onMouseEnter={() => setHoverDate?.(cloneDay)}
          onMouseLeave={() => setHoverDate?.(null)}
          className={`tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg ${compact ? 'tw-h-8 tw-w-8 tw-text-xs' : 'tw-h-10 tw-w-10 tw-text-sm'} ${!isSameMonth(cloneDay, monthStart) ? 'tw-text-gray-400' : ''} ${
            // whole range
            isRangeType && startDate && endDate && isDayInRange
              ? 'tw-bg-content-background tw-font-semibold tw-text-primary'
              : ''
          } ${
            // days after start date
            (isRangeType || isStartDate) &&
            isCandidateForEndDate &&
            !isHoveringRange
              ? 'tw-bg-base-200 tw-text-content-base/90'
              : ''
          } ${
            // range hover effect
            isRangeType && isHoveringRange
              ? 'tw-bg-content-background tw-font-semibold tw-text-content-base'
              : ''
          } ${
            // start end end dates
            selectedStart || selectedEnd
              ? 'tw-border-2 tw-border-primary tw-bg-content-background tw-font-semibold tw-text-primary'
              : ''
          } ${
            // start date
            startDate && !endDate && hoverDate && isSameDay(cloneDay, hoverDate)
              ? 'tw-border-2 tw-border-primary tw-font-semibold tw-text-primary hover:tw-bg-content-background'
              : !selectedStart && !selectedEnd
                ? 'hover:tw-bg-base-300'
                : ''
          } `}
        >
          {format(cloneDay, 'D')}
        </div>,
      )
      day = addDays(day, 1)
    }
  }

  return (
    <div className={`et-date-calendar ${compact ? '' : 'tw-px-4'}`}>
      <div
        className={`tw-grid tw-grid-cols-7 tw-text-center tw-text-xs tw-font-semibold tw-text-content-base/80 ${compact ? 'tw-gap-1' : 'tw-gap-2'}`}
      >
        {['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb', 'Nd'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div
        className={`tw-grid tw-grid-cols-7 tw-pt-2 ${compact ? 'tw-gap-1' : 'tw-gap-2'}`}
      >
        {days}
      </div>
    </div>
  )
}
